import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, Loader2, ChevronLeft, ChevronRight, Filter, AlertCircle } from 'lucide-react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import DatePicker from './ui/DatePicker';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { API_PATH, request } from '../config/api';
import styles from './CouponHistoryModal.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const SORT_OPTIONS = [
    { value: 'redeemed_at_desc', sortBy: 'redeemed_at', sortOrder: 'desc', label: 'Date (newest first)' },
    { value: 'redeemed_at_asc', sortBy: 'redeemed_at', sortOrder: 'asc', label: 'Date (oldest first)' },
    { value: 'plan_asc', sortBy: 'plan', sortOrder: 'asc', label: 'Plan (A–Z)' },
    { value: 'code_asc', sortBy: 'code', sortOrder: 'asc', label: 'Code (A–Z)' },
];
const PLAN_OPTIONS = [
    { value: '', label: 'All plans' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' },
    { value: 'platinum', label: 'Platinum' },
];

function getDefaultDateRange() {
    const to = new Date();
    const from = new Date(to);
    from.setMonth(from.getMonth() - 1);
    return {
        from: from.toISOString().slice(0, 10),
        to: to.toISOString().slice(0, 10),
    };
}

function valueLabel(row) {
    if (row.value_type === 'coins') return `${row.coin_value} coins`;
    if (row.subscription_days) return `${row.subscription_days} days`;
    if (row.subscription_end_date) return `Till ${formatDate(row.subscription_end_date)}`;
    return '—';
}

export default function CouponHistoryModal({ isOpen, onClose }) {
    const defaultRange = getDefaultDateRange();
    const [from, setFrom] = useState(defaultRange.from);
    const [to, setTo] = useState(defaultRange.to);
    const [planFilter, setPlanFilter] = useState('');
    const [sortValue, setSortValue] = useState('redeemed_at_desc');
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);

    const [data, setData] = useState({ redemptions: [], pagination: null, filter: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);
        try {
            const sortOpt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(pageSize),
                from,
                to,
                sortBy: sortOpt.sortBy,
                sortOrder: sortOpt.sortOrder,
            });
            if (planFilter) params.set('plan', planFilter);
            const res = await request(`${API_PATH}/auth/coupon-history?${params.toString()}`);
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(json.error || 'Failed to load coupon history');
                setData({ redemptions: [], pagination: null, filter: null });
                return;
            }
            setData({
                redemptions: json.redemptions || [],
                pagination: json.pagination || { page: 1, totalPages: 1, total: 0 },
                filter: json.filter,
            });
        } catch (e) {
            setError('Network error');
            setData({ redemptions: [], pagination: null, filter: null });
        } finally {
            setLoading(false);
        }
    }, [isOpen, page, pageSize, from, to, sortValue, planFilter]);

    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setFrom(defaultRange.from);
            setTo(defaultRange.to);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) fetchHistory();
    }, [isOpen, fetchHistory]);

    const handleApplyFilter = () => {
        setPage(1);
    };

    const sortOpt = SORT_OPTIONS.find((o) => o.value === sortValue) || SORT_OPTIONS[0];
    const { redemptions, pagination } = data;
    const totalPages = pagination?.totalPages ?? 1;
    const total = pagination?.total ?? 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Coupon history"
            size="xl"
            className={styles.modal}
        >
            <div className={styles.container}>
                <div className={styles.filters} role="region" aria-label="Filters">
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>
                            <Filter size={14} aria-hidden /> Date range
                        </label>
                        <DatePicker value={from} onChange={setFrom} max={to} />
                        <span className={styles.filterTo}>to</span>
                        <DatePicker value={to} onChange={setTo} min={from} />
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Plan</label>
                        <select
                            className={styles.select}
                            value={planFilter}
                            onChange={(e) => setPlanFilter(e.target.value)}
                            aria-label="Filter by plan"
                        >
                            {PLAN_OPTIONS.map((opt) => (
                                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Sort by</label>
                        <select
                            className={styles.select}
                            value={sortValue}
                            onChange={(e) => setSortValue(e.target.value)}
                            aria-label="Sort by"
                        >
                            {SORT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterRow}>
                        <label className={styles.filterLabel}>Per page</label>
                        <select
                            className={styles.select}
                            value={pageSize}
                            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            aria-label="Rows per page"
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                    <Button variant="primary" size="sm" className={styles.applyBtn} onClick={handleApplyFilter} disabled={loading}>
                        Apply
                    </Button>
                </div>

                {error && (
                    <div className={styles.errorBanner} role="alert">
                        <AlertCircle size={16} aria-hidden />
                        <span>{error}</span>
                    </div>
                )}

                {loading ? (
                    <div className={styles.loading}>
                        <Loader2 size={28} className={styles.spinner} aria-hidden />
                        <span>Loading…</span>
                    </div>
                ) : (
                    <>
                        <div className={styles.tableWrap}>
                            <table className={styles.table} role="table">
                                <thead>
                                    <tr>
                                        <th scope="col">Redeemed at</th>
                                        <th scope="col">Code</th>
                                        <th scope="col">Plan</th>
                                        <th scope="col">Amount</th>
                                        <th scope="col">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {redemptions.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className={styles.emptyCell}>
                                                <div className={styles.emptyState}>
                                                    <Ticket size={28} className={styles.emptyIcon} aria-hidden />
                                                    <span>No coupon redemptions in this date range. Try a different range or filter.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        redemptions.map((r) => (
                                            <tr key={r.id}>
                                                <td className={styles.dateCell}>{formatDateTime(r.redeemed_at) || '—'}</td>
                                                <td className={styles.codeCell}><code>{r.code}</code></td>
                                                <td className={styles.planCell}>{r.plan}</td>
                                                <td>{r.amount}</td>
                                                <td className={styles.valueCell}>{valueLabel(r)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {total > 0 && (
                            <div className={styles.pagination} role="navigation" aria-label="Pagination">
                                <span className={styles.paginationSummary}>
                                    Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                                </span>
                                <div className={styles.paginationButtons}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1 || loading}
                                        icon={<ChevronLeft size={16} />}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </Button>
                                    <span className={styles.pageIndicator} aria-live="polite">
                                        Page {page} of {totalPages}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages || loading}
                                        iconRight={<ChevronRight size={16} />}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}
